import { AppModal } from "@/components/ui/AppModal/AppModal";
import {
  Form,
  FormSubmitButton,
  TextFormField,
  TextAreaFormField,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUpdateCanonicalFile } from "@/lib/data/react-query/files/canonical-files";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import type { CanonicalFile } from "@/lib/data/schemas/files";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const schema = z.object({
  name: z.string().trim().min(1, "Enter a file name.").max(255),
  description: z.string().max(10000),
});

export function EditCanonicalFileModal({
  file,
  onClose,
}: {
  file: CanonicalFile;
  onClose: () => void;
}) {
  const mutation = useUpdateCanonicalFile();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: file.name, description: file.description ?? "" },
  });
  const setOpen = (open: boolean) => {
    if (!open) onClose();
  };
  const formId = "edit-search-file";
  return (
    <Form {...form}>
      <AppModal
        open
        onOpenChange={setOpen}
        title="Edit file"
        description="Update the file name and description."
        isDirty={form.formState.isDirty}
        loading={mutation.isPending}
        footer={
          <div className="flex justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              loading={mutation.isPending}
              submitFormId={formId}
            />
          </div>
        }
      >
        <form
          id={formId}
          className="space-y-4"
          onSubmit={form.handleSubmit(async (values) => {
            try {
              await mutation.mutateAsync({
                id: file.id,
                revision: file.revision,
                name: values.name,
                description: values.description || null,
              });
              toast.success("File updated");
              onClose();
            } catch (error) {
              toast.error(
                fileOperationMessage(error, "Unable to update this file."),
              );
            }
          })}
        >
          <TextFormField register={form.register("name")} label="Name" />
          <TextAreaFormField
            register={form.register("description")}
            label="Description"
          />
        </form>
      </AppModal>
    </Form>
  );
}
